export const name = "JSON_VALUE_ARRAY";
export const summary = `Extracts a JSON array of scalar values and converts it to a SQL \`ARRAY<STRING>\` value.`;
export const reference = `
## JSON_VALUE_ARRAY

Extracts a JSON array of scalar values and converts it to a SQL \`ARRAY<STRING>\` value.

\`\`\`
JSON_VALUE_ARRAY(json_string_expr[, json_path])
\`\`\`

### 

\`\`\`
JSON_VALUE_ARRAY(json_expr[, json_path])
\`\`\`


### Description

Extracts a JSON array of scalar values and converts it to a SQL\`ARRAY<STRING>\` value. In addition, this function:

- Removes the outermost quotes and unescapes the values.
- Returns a SQL \`NULL\` if the selected value isn't an array or not an array containing only scalar values.
- Uses double quotes to escape invalid JSONPath characters in JSON keys. For example: \`"a.b"\`.

Arguments:

- \`json_string_expr\`: A JSON-formatted string. For example:

- \`json_expr\`: JSON. For example:

- \`json_path\`: The JSONPath. This identifies the data that you want to obtain from the input. If this optional parameter isn't provided, then the JSONPath \`$\` symbol is applied, which means that all of the data is analyzed.

There are differences between the JSON-formatted string and JSON input types. For details, see Differences between the JSON and JSON-formatted STRING types.

Caveats:

- A JSON \`null\` in the input array produces a SQL \`NULL\` as the output for JSON \`null\`. If the output contains a \`NULL\` array element, an error is produced because the final output can't be an array with \`NULL\` values.
- If a JSONPath matches an array that contains scalar objects and a JSON \`null\`, then the output of the function must be transformed because the final output can't be an array with \`NULL\` values.


### Return type

\`ARRAY<STRING>\`


### Examples

This extracts items in JSON to a string array:



\`\`\`
SELECT JSON_VALUE_ARRAY(
  JSON '{"fruits": ["apples", "oranges", "grapes"]}', '$.fruits'
  ) AS string_array;

/*---------------------------*
 | string_array |
 +---------------------------+
 | [apples, oranges, grapes] |
 *---------------------------*/
\`\`\`

The following example compares how results are returned for the\`JSON_QUERY_ARRAY\` and \`JSON_VALUE_ARRAY\` functions.



\`\`\`
SELECT JSON_QUERY_ARRAY('["apples", "oranges"]') AS json_array,
       JSON_VALUE_ARRAY('["apples", "oranges"]') AS string_array;

/*-----------------------+-------------------*
 | json_array | string_array |
 +-----------------------+-------------------+
 | ["apples", "oranges"] | [apples, oranges] |
 *-----------------------+-------------------*/
\`\`\`

This extracts the items in a JSON-formatted string to a string array:



\`\`\`
-- Strips the double quotes
SELECT JSON_VALUE_ARRAY('["foo", "bar", "baz"]', '$') AS string_array;

/*-----------------*
 | string_array |
 +-----------------+
 | [foo, bar, baz] |
 *-----------------*/
\`\`\`

This extracts a string array and converts it to an integer array:



\`\`\`
SELECT ARRAY(
  SELECT CAST(integer_element AS INT64)
  FROM UNNEST(
    JSON_VALUE_ARRAY('[1, 2, 3]', '$')
  ) AS integer_element
) AS integer_array;

/*---------------*
 | integer_array |
 +---------------+
 | [1, 2, 3] |
 *---------------*/
\`\`\`

These are equivalent:



\`\`\`
SELECT JSON_VALUE_ARRAY('{"fruits": ["apples", "oranges", "grapes"]}', '$.fruits') AS string_array;
SELECT JSON_VALUE_ARRAY('{"fruits": ["apples", "oranges", "grapes"]}', '$."fruits"') AS string_array;

-- The queries above produce the following result:
/*---------------------------*
 | string_array |
 +---------------------------+
 | [apples, oranges, grapes] |
 *---------------------------*/
\`\`\`

In cases where a JSON key uses invalid JSONPath characters, you can escape those characters using double quotes: \`" "\`. For example:



\`\`\`
SELECT JSON_VALUE_ARRAY('{"a.b": {"c": ["world"]}}', '$."a.b".c') AS hello;

/*---------*
 | hello |
 +---------+
 | [world] |
 *---------*/
\`\`\`

The following examples explore how invalid requests and empty arrays are handled:



\`\`\`
-- An error is thrown if you provide an invalid JSONPath.
SELECT JSON_VALUE_ARRAY('["foo", "bar", "baz"]', 'INVALID_JSONPath') AS result;

-- If the JSON-formatted string is invalid, then NULL is returned.
SELECT JSON_VALUE_ARRAY('}}', '$') AS result;

/*--------*
 | result |
 +--------+
 | NULL |
 *--------*/

-- If the JSON document is NULL, then NULL is returned.
SELECT JSON_VALUE_ARRAY(NULL, '$') AS result;

/*--------*
 | result |
 +--------+
 | NULL |
 *--------*/

-- If a JSONPath doesn't match anything, then the output is NULL.
SELECT JSON_VALUE_ARRAY('{"a": ["foo", "bar", "baz"]}', '$.b') AS result;

/*--------*
 | result |
 +--------+
 | NULL |
 *--------*/

-- If a JSONPath matches an object that isn't an array, then the output is NULL.
SELECT JSON_VALUE_ARRAY('{"a": "foo"}', '$') AS result;

/*--------*
 | result |
 +--------+
 | NULL |
 *--------*/

-- If a JSONPath matches an array of non-scalar objects, then the output is NULL.
SELECT JSON_VALUE_ARRAY('{"a": [{"b": "foo", "c": 1}, {"b": "bar", "c": 2}], "d": "baz"}', '$.a') AS result;

/*--------*
 | result |
 +--------+
 | NULL |
 *--------*/

-- If a JSONPath matches an array of mixed scalar and non-scalar objects,
-- then the output is NULL.
SELECT JSON_VALUE_ARRAY('{"a": [10, {"b": 20}]', '$.a') AS result;

/*--------*
 | result |
 +--------+
 | NULL |
 *--------*/

-- If a JSONPath matches an empty JSON array, then the output is an empty array instead of NULL.
SELECT JSON_VALUE_ARRAY('{"a": "foo", "b": []}', '$.b') AS result;

/*--------*
 | result |
 +--------+
 | [] |
 *--------*/

-- The following query produces and error because the final output can't be an
-- array with NULLs.
SELECT JSON_VALUE_ARRAY('["world", 1, null]') AS result;
\`\`\`


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/json_functions
`;
